"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkAllowedHosts = exports.allowIfNotDangerousHost = exports.allowIfAllowedHost = exports.NeedleWrapperException = exports.parseResponseBody = exports.needleWrapper = void 0;
const fs = require("fs");
const net = require("net");
const needle = require("needle");
const stream_1 = require("stream");
const retryableStatusCodes = [429, 503, 502, 504];
const retryableMessages = [
    "resource has been exhausted",
    "check quota",
    "too many requests",
    "rate limit",
    "quota exceeded",
    "service temporarily unavailable",
    "recurrent request failures",
    "please try again in",
];
// TODO: this is a temporary code that allows setting needle default timeout (alias for
// open_timeout) to check how it affects the stability of our system, and specifically
// if it helps reducing 'socket hang up' errors.
// More information: https://www.npmjs.com/package/needle
if (process.env.DOCKER_REGISTRY_V2_CLIENT_NEEDLE_OPEN_TIMEOUT) {
    const openTimeout = parseInt(process.env.DOCKER_REGISTRY_V2_CLIENT_NEEDLE_OPEN_TIMEOUT);
    if (Number.isInteger(openTimeout)) {
        needle.defaults({
            timeout: openTimeout,
        });
    }
}
/**
 * A wrapper function that uses `needle` for making HTTP requests,
 * and returns a response that matches what the response it used to get from `request` library
 * @param options request options
 */
async function needleWrapper(options, maxRetries, observer) {
    var _a, _b, _c, _d, _e;
    let uri = options.uri;
    const allowedHostResult = checkAllowedHosts(uri, {
        allowedHosts: options.allowedHosts,
        disallowDangerousHosts: options.disallowDangerousHosts,
    });
    if (!allowedHostResult.allowed) {
        throw new Error(`Refusing to connect to disallowed host ${uri} with reason ${allowedHostResult.denyReason}`);
    }
    // append query parameters
    if (options.qs) {
        for (const key in options.qs) {
            if (options.qs[key] !== undefined) {
                uri += `&${key}=${options.qs[key]}`;
            }
        }
        if (!uri.includes("?")) {
            uri = uri.replace("&", "?");
        }
    }
    const attemptLimit = maxRetries !== null && maxRetries !== void 0 ? maxRetries : 0;
    let response;
    let lastError;
    let attempt = 0;
    while (attempt <= attemptLimit) {
        attempt++;
        response = null;
        try {
            if (options.snykInternalOutputStream) {
                response = await stream(needle.get(uri, Object.assign(Object.assign({}, options), { 
                    // needle streams the response to file and memory as convenience if output is set.
                    // causes high memory usage for large binaries.
                    output: null, 
                    // we treat the response body as a binary stream
                    parse_response: false })), fs.createWriteStream(options.snykInternalOutputStream));
            }
            else {
                response = await needle("get", uri, options);
            }
            if (response && response.statusCode >= 300) {
                let message;
                if (((_b = (_a = response.body) === null || _a === void 0 ? void 0 : _a.errors) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                    // Some registries return errors with only  a 'code' field
                    // and no 'message'. As a result, accessing errors[0].message
                    // would return undefined, and String(undefined) produced the literal
                    // string "undefined" as the error message. We now fall back to the error
                    // code or a JSON representation of the error object.
                    const error = response.body.errors[0];
                    const rawMessage = (_d = (_c = error.message) !== null && _c !== void 0 ? _c : error.code) !== null && _d !== void 0 ? _d : error;
                    message =
                        typeof rawMessage === "string"
                            ? rawMessage
                            : JSON.stringify(rawMessage);
                }
                else {
                    message =
                        typeof response.body === "string"
                            ? response.body
                            : (_e = JSON.stringify(response.body)) !== null && _e !== void 0 ? _e : `HTTP ${response.statusCode}`;
                }
                // check if this HTTP error status should be retried with backoff
                if (attempt <= attemptLimit &&
                    shouldRetryWithBackoff({ statusCode: response.statusCode, message })) {
                    const backoffDelay = calculateBackoffDelay(attempt);
                    console.warn(`Rate limit/quota exhaustion detected (HTTP ${response.statusCode}). Retrying in ${backoffDelay}ms... (attempt ${attempt})`);
                    await new Promise(resolve => setTimeout(resolve, backoffDelay));
                    continue; // continue the retry loop, don't hit the throw down below
                }
                // not retryable (or we've hit the max retries), throw the exception
                throw new NeedleWrapperException(message, response.statusCode, response.headers);
            }
            // Success case - break out of retry loop
            break;
        }
        catch (err) {
            lastError = err;
            // Check if this is a rate limiting/quota exhaustion error that should be retried with backoff
            if (attempt <= attemptLimit && shouldRetryWithBackoff(err)) {
                const backoffDelay = calculateBackoffDelay(attempt);
                console.warn(`Rate limit/quota exhaustion detected (network error). Retrying in ${backoffDelay}ms... (attempt ${attempt})`);
                await new Promise(resolve => setTimeout(resolve, backoffDelay));
                continue;
            }
            // If this was an exception thrown from our status code handling above, re-throw it
            if (err instanceof NeedleWrapperException) {
                throw err;
            }
        }
    }
    if (!response) {
        throw lastError;
    }
    return response;
}
exports.needleWrapper = needleWrapper;
function parseResponseBody(response) {
    let body;
    try {
        body = JSON.parse(response.body);
    }
    catch (err) {
        body = response.body;
    }
    return body;
}
exports.parseResponseBody = parseResponseBody;
class NeedleWrapperException extends Error {
    constructor(message, statusCode, headers) {
        super(message);
        this.statusCode = statusCode;
        this.headers = headers;
    }
}
exports.NeedleWrapperException = NeedleWrapperException;
/**
 * Streams the readable stream to the writeable stream and returns the response
 * of the underlying http.ClientRequest.
 */
async function stream(readable, writeable) {
    return new Promise((resolve, reject) => {
        let response;
        // Emitted when the underlying http.ClientRequest emits a response event.
        // This is after the connection is established and the header received, but
        // before any of it is processed (e.g. authorization required or redirect
        // to be followed). No data has been consumed at this point.
        readable.on("response", inner => {
            response = inner;
        });
        // Triggered after the header has been processed, and just before the data
        // is to be consumed. This implies that no redirect was followed and/or
        // authentication header was received. In other words, we got a "valid"
        // response.
        readable.on("header", (statusCode, headers) => {
            response.statusCode = statusCode;
            response.headers = headers;
        });
        // Emitted when an error ocurrs. This should only happen once in the
        // lifecycle of a Needle request.
        readable.on("err", err => {
            reject(err);
        });
        // Emitted when an timeout error occurs. Type can be either 'open',
        // 'response', or 'read'. This will called right before aborting the
        // request, which will also trigger an err event, a described above, with
        // an ECONNRESET (Socket hang up) exception.
        readable.on("timeout", type => {
            reject(new Error(type + " timeout"));
        });
        (0, stream_1.pipeline)(readable, writeable, err => err ? reject(err) : resolve(response));
    });
}
function shouldRetryWithBackoff(error) {
    // Google Artifact Registry and other providers use these status codes for rate limiting
    if (!retryableStatusCodes.includes(error.statusCode)) {
        return false;
    }
    // Check for quota exhaustion messages in both message and response body
    const message = (error.message || error.body || "").toString().toLowerCase();
    return retryableMessages.some(msg => message.includes(msg));
}
function calculateBackoffDelay(attempt) {
    // exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at 16s)
    return Math.min(1000 * Math.pow(2, attempt - 1), 16000);
}
function allowIfAllowedHost(host, allowedHosts) {
    if (allowedHosts.includes(host)) {
        return { allowed: true };
    }
    return {
        allowed: false,
        denyReason: "host disallowed by allowed hosts filter",
    };
}
exports.allowIfAllowedHost = allowIfAllowedHost;
function allowIfNotDangerousHost(host) {
    // Disallow IPv4 addresses
    if (net.isIPv4(host)) {
        return {
            allowed: false,
            denyReason: "host disallowed by IPv4 address filter",
        };
    }
    // Disallow IPv6 addresses
    if (/^\[.*\]$/.test(host) && net.isIPv6(host.slice(1, -1))) {
        return {
            allowed: false,
            denyReason: "host disallowed by IPv6 address filter",
        };
    }
    // Disallow hosts in the cluster.local subdomain
    if (/cluster\.local$/.test(host)) {
        return {
            allowed: false,
            denyReason: "host disallowed by cluster.local subdomains filter",
        };
    }
    // Disallow hosts without a TLD+1
    if (!host.includes(".")) {
        return {
            allowed: false,
            denyReason: "host disallowed by require TLD+1 filter",
        };
    }
    return { allowed: true };
}
exports.allowIfNotDangerousHost = allowIfNotDangerousHost;
function checkAllowedHosts(uri, options) {
    let host;
    try {
        host = new URL(uri).hostname;
    }
    catch (err) {
        throw new Error(`Failed to parse host from uri: ${uri}`);
    }
    // If allowedHosts is set, deny any host that is not in the list.
    if (options.allowedHosts) {
        const allowedHostResult = allowIfAllowedHost(host, options.allowedHosts);
        if (!allowedHostResult.allowed) {
            return allowedHostResult;
        }
    }
    // If disallowDangerousHosts is set, deny IP addresses, hosts in the
    // cluster.local subdomain, and hosts without a TLD+1.
    if (options.disallowDangerousHosts) {
        return allowIfNotDangerousHost(host);
    }
    return { allowed: true };
}
exports.checkAllowedHosts = checkAllowedHosts;
//# sourceMappingURL=needle.js.map